import jsPDF from "jspdf";

interface PDFData {
  transaction: any;
  items: any[];
  payments: any[];
  client: any;
  seller: any;
  tenant: any;
}

export async function generateA4PDF(data: PDFData) {
  const { transaction, items, payments, client, seller, tenant } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let yPos = margin;

  // Helper para adicionar texto com quebra de linha
  const addText = (text: string, x: number, y: number, options: any = {}) => {
    const maxWidth = options.maxWidth || pageWidth - margin * 2;
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return lines.length * (options.lineHeight || 7);
  };

  // Helper para adicionar texto centralizado
  const addCenteredText = (text: string, y: number, fontSize: number = 10, bold: boolean = false) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    const textWidth = doc.getTextWidth(text);
    doc.text(text, (pageWidth - textWidth) / 2, y);
    return fontSize * 0.35;
  };

  // Header
  yPos += addCenteredText(tenant?.name || "Empresa", yPos, 20, true);
  yPos += 2.5;

  if (tenant?.email) {
    yPos += addCenteredText(tenant.email, yPos, 10, false);
  }

  yPos += 5;
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 7.5;

  // Informações da Venda
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  yPos += addText("Informações da Venda", margin, yPos);
  yPos += 4;

  doc.setFontSize(10);
  
  // Primeira linha: Número (esquerda), Data da Venda (centro), Data de Entrega (direita)
  const numeroLabel = "Número:";
  const numeroValor = transaction?.id?.substring(0, 8) || "N/A";
  const numeroTexto = `${numeroLabel} ${numeroValor}`;
  
  const dataVendaLabel = "Data da Venda:";
  const dataVendaValor = transaction?.data
    ? new Date(transaction.data).toLocaleDateString("pt-BR")
    : new Date().toLocaleDateString("pt-BR");
  const dataVendaTexto = `${dataVendaLabel} ${dataVendaValor}`;
  
  const dataEntregaLabel = "Data de Entrega:";
  const dataEntregaValor = transaction?.data_entrega
    ? new Date(transaction.data_entrega).toLocaleDateString("pt-BR")
    : null;
  const dataEntregaTexto = dataEntregaValor ? `${dataEntregaLabel} ${dataEntregaValor}` : "";
  
  // Esquerda: Número
  doc.setFont("helvetica", "bold");
  doc.text(numeroLabel, margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${numeroValor}`, margin + doc.getTextWidth(numeroLabel), yPos);
  
  // Centro: Data da Venda
  const dataVendaWidth = doc.getTextWidth(dataVendaTexto);
  const centerX = (pageWidth - dataVendaWidth) / 2;
  doc.setFont("helvetica", "bold");
  doc.text(dataVendaLabel, centerX, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${dataVendaValor}`, centerX + doc.getTextWidth(dataVendaLabel), yPos);
  
  // Direita: Data de Entrega
  if (dataEntregaValor) {
    const dataEntregaWidth = doc.getTextWidth(dataEntregaTexto);
    const rightX = pageWidth - margin - dataEntregaWidth;
    doc.setFont("helvetica", "bold");
    doc.text(dataEntregaLabel, rightX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${dataEntregaValor}`, rightX + doc.getTextWidth(dataEntregaLabel), yPos);
  }
  yPos += 7;

  // Segunda linha: Cliente (esquerda), Telefone (centro), Email (direita)
  const clienteLabel = "Cliente:";
  const clienteTexto = client?.nome || "N/A";
  const clienteCompleto = `${clienteLabel}   ${clienteTexto}`;
  
  const telefoneLabel = "Telefone:";
  const telefoneTexto = client?.telefone || "";
  const telefoneCompleto = telefoneTexto ? `${telefoneLabel}   ${telefoneTexto}` : "";
  
  const emailLabel = "Email:";
  const emailTexto = client?.email || "";
  const emailCompleto = emailTexto ? `${emailLabel}   ${emailTexto}` : "";
  
  // Esquerda: Cliente
  doc.setFont("helvetica", "bold");
  doc.text(clienteLabel, margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${clienteTexto}`, margin + doc.getTextWidth(clienteLabel), yPos);
  
  // Centro: Telefone
  if (telefoneTexto) {
    const telefoneWidth = doc.getTextWidth(telefoneCompleto);
    const centerX = (pageWidth - telefoneWidth) / 2;
    doc.setFont("helvetica", "bold");
    doc.text(telefoneLabel, centerX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${telefoneTexto}`, centerX + doc.getTextWidth(telefoneLabel), yPos);
  }
  
  // Direita: Email
  if (emailTexto) {
    const emailWidth = doc.getTextWidth(emailCompleto);
    const rightX = pageWidth - margin - emailWidth;
    doc.setFont("helvetica", "bold");
    doc.text(emailLabel, rightX, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${emailTexto}`, rightX + doc.getTextWidth(emailLabel), yPos);
  }
  yPos += 7;

  // Terceira linha: Vendedor
  const vendedorLabel = "Vendedor:";
  const vendedorValor = seller?.nome_completo || "N/A";
  doc.setFont("helvetica", "bold");
  doc.text(vendedorLabel, margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${vendedorValor}`, margin + doc.getTextWidth(vendedorLabel), yPos);
  yPos += 7;

  // Descrição (se houver, pode ocupar múltiplas linhas)
  if (transaction?.descricao) {
    yPos += addText(`Descrição: ${transaction.descricao}`, margin, yPos);
  }

  yPos += 10;

  // Itens da Venda
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Itens da Venda", margin, yPos);
  yPos += 8;

  // Tabela de Itens
  const tableTop = yPos;
  const colWidths = {
    produto: (pageWidth - margin * 2) * 0.5,
    qtd: (pageWidth - margin * 2) * 0.15,
    preco: (pageWidth - margin * 2) * 0.15,
    total: (pageWidth - margin * 2) * 0.2,
  };

  let xPos = margin;

  // Cabeçalho da tabela
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Produto", xPos, yPos);
  xPos += colWidths.produto;
  doc.text("Qtd", xPos, yPos);
  xPos += colWidths.qtd;
  doc.text("Preço Unit.", xPos, yPos);
  xPos += colWidths.preco;
  doc.text("Total", xPos, yPos);

  yPos += 5;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  // Função auxiliar para formatar valor do campo do formulário
  const normalizeType = (t: string) => {
    const s = (t || "").toLowerCase();
    if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
    if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
    if (["radio"].includes(s)) return "radio";
    if (["checkbox"].includes(s)) return "checkbox";
    if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
    if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
    if (["data", "date"].includes(s)) return "date";
    if (["data e hora", "datetime", "datahora"].includes(s)) return "datetime";
    return s;
  };

  const formatBRDate = (v: any, withTime = false): string => {
    if (!v) return "";
    if (typeof v === "string") {
      const s = v.trim();
      if (s.length >= 10 && s.includes("-")) {
        const [y, m, dAndTime] = s.split("-");
        const d = dAndTime.slice(0, 2);
        const rest = dAndTime.slice(2);
        const base = `${d}/${m}/${y}`;
        if (withTime && s.includes("T")) {
          const time = s.split("T")[1].slice(0, 5);
          return `${base} ${time}`;
        }
        return base;
      }
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        const dd = String(dt.getDate()).padStart(2, "0");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const yyyy = dt.getFullYear();
        const base = `${dd}/${mm}/${yyyy}`;
        if (withTime) {
          const hh = String(dt.getHours()).padStart(2, "0");
          const min = String(dt.getMinutes()).padStart(2, "0");
          return `${base} ${hh}:${min}`;
        }
        return base;
      }
      return s;
    }
    try {
      const dt = new Date(v);
      if (!isNaN(dt.getTime())) {
        const dd = String(dt.getDate()).padStart(2, "0");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const yyyy = dt.getFullYear();
        const base = `${dd}/${mm}/${yyyy}`;
        if (withTime) {
          const hh = String(dt.getHours()).padStart(2, "0");
          const min = String(dt.getMinutes()).padStart(2, "0");
          return `${base} ${hh}:${min}`;
        }
        return base;
      }
    } catch {}
    return String(v);
  };

  const formatFormFieldValue = (field: any, value: any): string => {
    if (!value && value !== 0) return "";

    if (typeof value === "string") {
      const s = value.trim();
      if ((s.startsWith("[") && s.endsWith("]")) || (s.startsWith("{") && s.endsWith("}"))) {
        try { value = JSON.parse(s); } catch {}
      }
    }

    switch (normalizeType(field.tipo)) {
      case "select":
      case "radio":
        // Buscar opção pelo valor
        const option = field.form_field_options?.find((opt: any) => opt.id === value || opt.valor === value);
        return option ? option.label : String(value);
      
      case "date":
        return formatBRDate(value, false);
      case "datetime":
        return formatBRDate(value, true);
      
      case "multiselect":
      case "checkbox":
        // Array de valores
        if (Array.isArray(value)) {
          return value
            .map((v) => {
              const opt = field.form_field_options?.find((opt: any) => opt.id === v || opt.valor === v);
              return opt ? opt.label : String(v);
            })
            .join(", ");
        }
        return String(value);
      
      case "file":
      case "image":
        if (value && typeof value === "object") {
          const name = (value as any).original_name || (value as any).path || "arquivo";
          return name;
        }
        return String(value);
      
      default:
        return String(value);
    }
  };

  // Linhas dos itens
  doc.setFont("helvetica", "normal");
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = margin;
    }

    // Adicionar linha separadora mais fina ANTES do produto (exceto o primeiro)
    if (index > 0) {
      // Espaçamento antes do traço (margin-top reduzido)
      yPos += 0.2;
      // Desenhar o traço
      doc.setLineWidth(0.1);
      doc.setDrawColor(200, 200, 200); // Cinza claro
      doc.line(margin, yPos, pageWidth - margin, yPos);
      // Espaçamento abaixo do traço (margin-bottom aumentado) antes do produto
      yPos += 5;
    }

    xPos = margin;
    const productName = item.product?.nome || "Produto";
    
    // Nome do produto em negrito
    doc.setFont("helvetica", "bold");
    const nameLines = doc.splitTextToSize(productName, colWidths.produto - 2);
    doc.text(nameLines, xPos, yPos);
    let itemHeight = nameLines.length * 5;
    
    // Quantidade, Preço Unit. e Total na mesma linha do produto (alinhados à direita)
    xPos += colWidths.produto;
    doc.setFont("helvetica", "normal");
    const qtdText = item.quantidade.toString();
    const qtdWidth = doc.getTextWidth(qtdText);
    doc.text(qtdText, xPos + colWidths.qtd - qtdWidth, yPos);
    xPos += colWidths.qtd;

    const precoUnitText = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(item.preco_unitario);
    const precoUnitWidth = doc.getTextWidth(precoUnitText);
    doc.text(precoUnitText, xPos + colWidths.preco - precoUnitWidth, yPos);
    xPos += colWidths.preco;

    const totalText = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(item.total);
    const totalWidth = doc.getTextWidth(totalText);
    doc.text(totalText, xPos + colWidths.total - totalWidth, yPos);
    
    // Imagens vinculadas aos campos (image ou file com mime image/*)
    if (item.form_fields && item.form_fields.length > 0 && item.form_values) {
      const imageEntries = item.form_fields
        .map((field: any) => ({ field, value: item.form_values?.[field.slug] }))
        .filter(({ field, value }) => {
          if (!value) return false;
          const isFieldImage = field.tipo === "image";
          const isFileImage = field.tipo === "file" && typeof value === "object" && ((value?.mime_type || "").startsWith("image/"));
          return isFieldImage || isFileImage;
        }) as Array<{ field: any; value: any }>;

      const loadImageDataUrl = async (url: string) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const reader = new FileReader();
          const dataUrl: string = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = resolve;
            img.src = dataUrl;
          });
          return { dataUrl, widthPx: img.naturalWidth || 300, heightPx: img.naturalHeight || 300 };
        } catch {
          return null;
        }
      };

      if (imageEntries.length > 0) {
        const maxImageWidthMm = colWidths.produto - 4;
        // Começar logo abaixo da altura ocupada pelo nome do produto
        let currentY = yPos + itemHeight + 2;
        for (const entry of imageEntries) {
          const url = (entry.value?.signed_url as string) || "";
          if (!url) continue;
          const imgData = await loadImageDataUrl(url);
          if (!imgData) continue;
          const ratio = imgData.heightPx / imgData.widthPx;
          const imgWidthMm = Math.min(maxImageWidthMm, 40);
          const imgHeightMm = imgWidthMm * ratio;
          doc.addImage(imgData.dataUrl, undefined as any, margin + 2, currentY, imgWidthMm, imgHeightMm);
          currentY += imgHeightMm + 3; // espaço entre imagens
        }
        itemHeight = Math.max(itemHeight, currentY - yPos);
      }

      // Valores dos campos do formulário na linha seguinte, usando toda a largura
      // (após imagens, se houver)
      if (item.form_fields && item.form_fields.length > 0 && item.form_values) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        // Posicionar abaixo do maior conteúdo (nome/imagens)
        let formFieldsY = yPos + itemHeight + 1;
        
        const formFieldsWithValues = item.form_fields.filter((field: any) => 
          item.form_values?.[field.slug] !== undefined && 
          item.form_values?.[field.slug] !== null && 
          item.form_values?.[field.slug] !== ""
        );
        
        if (formFieldsWithValues.length > 0) {
          // Construir texto com labels em negrito
          const totalWidth = pageWidth - margin * 2;
          let currentX = margin + 2;
          let currentY = formFieldsY;
          
          formFieldsWithValues.forEach((field: any, fieldIndex: number) => {
            const value = item.form_values[field.slug];
            const formattedValue = formatFormFieldValue(field, value);
            
            // Label em negrito
            doc.setFont("helvetica", "bold");
            const labelText = `${field.nome}: `;
            const labelWidth = doc.getTextWidth(labelText);
            
            // Verificar se cabe na linha atual
            if (currentX + labelWidth + doc.getTextWidth(formattedValue) > pageWidth - margin - 2) {
              currentY += 4;
              currentX = margin + 2;
            }
            
            doc.text(labelText, currentX, currentY);
            currentX += labelWidth;
            
            // Valor em normal
            doc.setFont("helvetica", "normal");
            const valueText = formattedValue;
            const valueWidth = doc.getTextWidth(valueText);
            
            // Verificar se o valor cabe
            if (currentX + valueWidth > pageWidth - margin - 2) {
              const valueLines = doc.splitTextToSize(valueText, totalWidth - (currentX - margin));
              doc.text(valueLines, currentX, currentY);
              currentY += valueLines.length * 4;
              currentX = margin + 2;
            } else {
              doc.text(valueText, currentX, currentY);
              currentX += valueWidth;
            }
            
            // Separador
            if (fieldIndex < formFieldsWithValues.length - 1) {
              doc.setFont("helvetica", "normal");
              const separator = " • ";
              const separatorWidth = doc.getTextWidth(separator);
              
              if (currentX + separatorWidth > pageWidth - margin - 2) {
                currentY += 4;
                currentX = margin + 2;
              } else {
                doc.text(separator, currentX, currentY);
                currentX += separatorWidth;
              }
            }
          });
          
          formFieldsY = currentY + 6; // Aumentar espaço após formulário
          itemHeight = Math.max(itemHeight, formFieldsY - yPos);
          doc.setFontSize(10);
          
          // Atualizar yPos para a posição final do formulário
          yPos = formFieldsY;
        } else {
          // Se não houver dados de formulário, usar espaçamento reduzido
          doc.setFontSize(10);
          yPos += Math.max(itemHeight, 5); // Espaçamento menor quando não há formulário
        }
      }
    } else {
      // Quando não há form_fields ou form_values, usar espaçamento reduzido
      yPos += Math.max(itemHeight, 5); // Espaçamento menor quando não há formulário
    }
  }

  yPos += 10;

  // Pagamentos
  if (payments.length > 0) {
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Pagamentos", margin, yPos);
    yPos += 8;

    const paymentColWidths = {
      data: (pageWidth - margin * 2) * 0.25,
      metodo: (pageWidth - margin * 2) * 0.35,
      valor: (pageWidth - margin * 2) * 0.2,
      status: (pageWidth - margin * 2) * 0.2,
    };

    // Cabeçalho
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    xPos = margin;
    doc.text("Data", xPos, yPos);
    xPos += paymentColWidths.data;
    doc.text("Método", xPos, yPos);
    xPos += paymentColWidths.metodo;
    doc.text("Valor", xPos, yPos);
    xPos += paymentColWidths.valor;
    doc.text("Status", xPos, yPos);

    yPos += 5;
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // Linhas dos pagamentos
    doc.setFont("helvetica", "normal");
    payments.forEach((payment) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = margin;
      }

      xPos = margin;
      // Usar payment.data se disponível, senão usar created_at ou confirmed_at
      const paymentDate = payment.data || payment.confirmed_at || payment.created_at;
      if (paymentDate) {
        doc.text(
          new Date(paymentDate).toLocaleDateString("pt-BR"),
          xPos,
          yPos
        );
      } else {
        doc.text("N/A", xPos, yPos);
      }
      xPos += paymentColWidths.data;

      doc.text(payment.payment_method?.nome || "N/A", xPos, yPos);
      xPos += paymentColWidths.metodo;

      doc.text(
        new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(payment.valor),
        xPos,
        yPos
      );
      xPos += paymentColWidths.valor;

      if (payment.status === "confirmado") {
        doc.setTextColor(0, 128, 0);
      } else {
        doc.setTextColor(255, 165, 0);
      }
      doc.text(
        payment.status === "confirmado" ? "Confirmado" : "Pendente",
        xPos,
        yPos
      );
      doc.setTextColor(0, 0, 0);

      yPos += 7;
    });

    yPos += 10;
  }

  // Totais
  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = margin;
  }

  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const desconto = transaction?.desconto || 0;
  const total = subtotal - desconto;
  const totalPago = payments
    .filter((p) => p.status === "confirmado")
    .reduce((sum, p) => sum + p.valor, 0);
  const saldo = total - totalPago;

  const totalsX = pageWidth - margin - 80;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Subtotal:", totalsX, yPos);
  const subtotalText = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(subtotal);
  const subtotalTextWidth = doc.getTextWidth(subtotalText);
  doc.text(subtotalText, pageWidth - margin - subtotalTextWidth, yPos);
  yPos += 7;

  if (desconto > 0) {
    doc.text("Desconto:", totalsX, yPos);
    const descontoText = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(desconto);
    const descontoTextWidth = doc.getTextWidth(descontoText);
    doc.text(descontoText, pageWidth - margin - descontoTextWidth, yPos);
    yPos += 7;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("Total:", totalsX, yPos);
  const totalText = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(total);
  const totalTextWidth = doc.getTextWidth(totalText);
  doc.text(totalText, pageWidth - margin - totalTextWidth, yPos);
  yPos += 7;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Total Pago:", totalsX, yPos);
  const totalPagoText = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(totalPago);
  const totalPagoTextWidth = doc.getTextWidth(totalPagoText);
  doc.text(totalPagoText, pageWidth - margin - totalPagoTextWidth, yPos);
  yPos += 7;

  if (saldo > 0) {
    doc.setTextColor(255, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.text("Saldo:", totalsX, yPos);
    const saldoText = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(saldo);
    const saldoTextWidth = doc.getTextWidth(saldoText);
    doc.text(saldoText, pageWidth - margin - saldoTextWidth, yPos);
    doc.setTextColor(0, 0, 0);
  }

  // Footer
  const footerY = pageHeight - 15;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(128, 128, 128);
  const footerText = `${tenant?.name || "Empresa"}${tenant?.email ? ` - ${tenant.email}` : ""}`;
  const footerTextWidth = doc.getTextWidth(footerText);
  doc.text(footerText, (pageWidth - footerTextWidth) / 2, footerY);
  doc.setTextColor(0, 0, 0);

  // Abrir PDF no navegador
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
  // Limpar o URL após um tempo para liberar memória
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
}

